import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWordPressHomepageFields } from "@/hooks/useWordPress";
import { parseVideoSource } from "@/lib/video";
import { Link } from "react-router-dom";

interface HeroProps {
  onDemoClick?: () => void;
}

/** Render a CTA as a link (if URL provided) or button (fallback to onClick) */
const CtaButton = ({
  label,
  url,
  target,
  onClick,
  variant = "default",
  className,
}: {
  label: string;
  url?: string;
  target?: string;
  onClick: () => void;
  variant?: "default" | "outline";
  className: string;
}) => {
  if (!label) return null;
  if (url) {
    const isExternal = url.startsWith("http");
    if (isExternal) {
      return (
        <a href={url} target={target || "_self"} rel={target === "_blank" ? "noopener noreferrer" : undefined}>
          <Button variant={variant} size="lg" className={className}>{label}</Button>
        </a>
      );
    }
    return (
      <Link to={url}>
        <Button variant={variant} size="lg" className={className}>{label}</Button>
      </Link>
    );
  }
  return (
    <Button variant={variant} size="lg" onClick={onClick} className={className}>{label}</Button>
  );
};

/** Map CMS overlay color key to CSS color */
const OVERLAY_COLORS: Record<string, string> = {
  black: '0, 0, 0',
  dark_blue: '10, 15, 40',
  dark_purple: '25, 10, 40',
};

/** Video background — defers iframe mount until after first paint so hero text + nav appear instantly */
const HeroVideoBackground = ({
  videoUrl,
  videoPoster,
  overlayColor,
  overlayOpacity,
}: {
  videoUrl: string;
  videoPoster: string;
  overlayColor?: string;
  overlayOpacity?: number;
}) => {
  const [loadMedia, setLoadMedia] = useState(false);

  useEffect(() => {
    if (!videoUrl) return;
    const schedule = typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback
      : (cb: () => void) => setTimeout(cb, 1500);
    const id = schedule(() => setLoadMedia(true));
    return () => {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(id as number);
      } else {
        clearTimeout(id as number);
      }
    };
  }, [videoUrl]);

  const videoSource = parseVideoSource(videoUrl);
  const isIframe = (videoSource.type === "vimeo" || videoSource.type === "youtube") && videoSource.id;

  const hasCmsOverlay = overlayColor && overlayColor !== 'none';
  const overlayRgb = hasCmsOverlay ? (OVERLAY_COLORS[overlayColor] || '0, 0, 0') : null;
  const opacityValue = overlayOpacity != null ? overlayOpacity / 100 : 0.5;

  return (
    <div className="absolute inset-0">
      {videoPoster ? (
        <img
          src={videoPoster}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
          decoding="async"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
      )}

      {loadMedia && isIframe && (
        <iframe
          src={videoSource.url}
          allow="autoplay; encrypted-media"
          allowFullScreen
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300%] h-[300%] min-w-full min-h-full pointer-events-none"
          style={{ border: 0 }}
          title="Hero background video"
        />
      )}

      {loadMedia && !isIframe && videoUrl && (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          poster={videoPoster}
        >
          <source src={videoUrl} type="video/mp4" />
          <track kind="captions" />
        </video>
      )}

      {overlayRgb ? (
        <div
          className="absolute inset-0"
          style={{ backgroundColor: `rgba(${overlayRgb}, ${opacityValue})` }}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />
      )}
    </div>
  );
};

/* Hero now uses the shared homepageFields query instead of a separate heroSettings query,
   eliminating one duplicate CMS round-trip on homepage load. */

const Hero = ({ onDemoClick }: HeroProps) => {
  const handleDemoClick = onDemoClick || (() => { window.location.href = '/contact'; });
  const { data: homepageFields, isLoading } = useWordPressHomepageFields();

  // Extract hero fields from homepageFields (same data, no extra network request)
  const content = {
    headline: homepageFields?.heroHeadline || "",
    subheadline: homepageFields?.heroSubheadline || "",
    ctaPrimary: homepageFields?.heroCtaPrimary || "",
    ctaSecondary: homepageFields?.heroCtaSecondary || "",
    ctaPrimaryUrl: homepageFields?.heroCtaPrimaryUrl,
    ctaPrimaryTarget: homepageFields?.heroCtaPrimaryTarget,
    ctaSecondaryUrl: homepageFields?.heroCtaSecondaryUrl,
    ctaSecondaryTarget: homepageFields?.heroCtaSecondaryTarget,
    videoUrl: homepageFields?.heroVideoUrl || "",
    videoPoster: (() => {
      const poster = homepageFields?.heroVideoPoster;
      if (typeof poster === 'string') return poster;
      if (poster && typeof poster === 'object' && 'node' in (poster as any)) {
        return (poster as any).node?.sourceUrl || "";
      }
      return "";
    })(),
  };

  const overlayColor = homepageFields?.heroOverlayColor as string | undefined;
  const overlayOpacity = homepageFields?.heroOverlayOpacity != null ? Number(homepageFields.heroOverlayOpacity) : undefined;

  const scrollToWork = () => {
    const workSection = document.getElementById("work");
    workSection?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative h-screen w-full overflow-hidden bg-black">
      <HeroVideoBackground
        videoUrl={content.videoUrl}
        videoPoster={content.videoPoster}
        overlayColor={overlayColor}
        overlayOpacity={overlayOpacity}
      />

      <div className={`relative z-10 flex h-full flex-col items-center justify-center px-6 transition-opacity duration-500 ${isLoading ? "opacity-0" : "opacity-100"}`}>
        <div className="text-center max-w-5xl animate-[fadeIn_1.2s_ease-out_both]">
          <h1 className="text-display mb-6 text-white drop-shadow-[0_4px_30px_rgba(0,0,0,0.5)] animate-[fadeIn_1s_0.2s_ease-out_both]">
            {content.headline}
          </h1>

          <p className="text-subheadline text-white/90 mb-12 max-w-3xl mx-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)] animate-[fadeIn_0.8s_0.5s_ease-out_both]">
            {content.subheadline}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-[fadeIn_0.8s_0.8s_ease-out_both]">
            <CtaButton
              label={content.ctaPrimary}
              url={content.ctaPrimaryUrl}
              target={content.ctaPrimaryTarget}
              onClick={handleDemoClick}
              className="bg-white text-black hover:bg-white/90 px-8 py-6 text-lg font-semibold shadow-lg transition-all duration-300"
            />
            <CtaButton
              label={content.ctaSecondary}
              url={content.ctaSecondaryUrl}
              target={content.ctaSecondaryTarget}
              onClick={scrollToWork}
              variant="outline"
              className="border-white/50 text-white hover:bg-white/10 px-8 py-6 text-lg font-medium backdrop-blur-sm"
            />
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-[fadeIn_0.8s_1.2s_ease-out_both]">
          <button
            onClick={scrollToWork}
            className="flex flex-col items-center gap-2 text-white/70 hover:text-white transition-colors min-w-[44px] min-h-[44px]"
            aria-label="Scroll down to see our work"
          >
            <span className="text-sm tracking-widest uppercase">Scroll</span>
            <ChevronDown className="w-6 h-6" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;